import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import Pagination from '../components/Pagination';

const ROLE_OPTIONS = [
  { value: '', label: 'ロール' },
  { value: 'user', label: '一般ユーザー' },
  { value: 'admin', label: '管理者' },
  { value: 'store_owner', label: '店舗オーナー' },
  { value: 'maker', label: 'メーカー' },
];

const ROLE_LABELS = {
  admin: '管理者',
  user: '一般',
  store_owner: 'オーナー',
  maker: 'メーカー',
};

export default function UserList() {
  useAuthGuard();

  const [sp, setSp] = useSearchParams();
  const initQ = sp.get('q') || '';
  const initRole = sp.get('role') || '';

  const [keyword, setKeyword] = useState(initQ);
  const [queryKeyword, setQueryKeyword] = useState(initQ);
  const [roleFilter, setRoleFilter] = useState(initRole);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (queryKeyword) p.set('q', queryKeyword);
    if (roleFilter) p.set('role', roleFilter);
    setSp(p, { replace: true });
  }, [queryKeyword, roleFilter, setSp]);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = new URL(`${API_BASE_URL}/admin/users`);
        url.searchParams.set('limit', '20');
        if (cursor) url.searchParams.set('cursor', cursor);
        if (queryKeyword.trim()) url.searchParams.set('q', queryKeyword.trim());
        if (roleFilter) url.searchParams.set('role', roleFilter);

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const list = data.data || [];
        setItems(Array.isArray(list) ? list : []);
        setHasMore(data.meta?.hasMore ?? false);
        setTotal(data.meta?.total ?? null);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [cursor, queryKeyword, roleFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCursor('');
    setCursorHistory([]);
    setQueryKeyword(keyword.trim());
  };

  const handleClear = () => {
    setKeyword('');
    setQueryKeyword('');
    setRoleFilter('');
    setCursor('');
    setCursorHistory([]);
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCursor('');
    setCursorHistory([]);
  };

  const handleNext = () => {
    if (items.length > 0) {
      const lastItem = items[items.length - 1];
      setCursorHistory((prev) => [...prev, cursor]);
      setCursor(lastItem.id || lastItem.user_id || '');
    }
  };

  const handlePrev = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setCursor(prevCursor || '');
      return next;
    });
  };

  const fmtDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const hasActiveFilters = queryKeyword || roleFilter;

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="page-title">ユーザー管理</h2>
        <p className="page-subtitle">ユーザーの検索・管理</p>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <form onSubmit={handleSearch} className="d-flex align-items-center gap-2 flex-wrap">
            <div className="search-box" style={{ minWidth: '200px', maxWidth: '300px', flex: '1' }}>
              <i className="search-icon" data-feather="search" style={{ width: 14, height: 14 }}></i>
              <input
                type="search"
                className="form-control search-input"
                placeholder="メールアドレス・表示名で検索"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            <select
              className="form-select"
              value={roleFilter}
              onChange={handleFilterChange(setRoleFilter)}
              style={{ maxWidth: '160px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button type="submit" className="btn btn-phoenix-primary btn-sm">検索</button>
            {hasActiveFilters && (
              <button type="button" className="btn btn-phoenix-secondary btn-sm" onClick={handleClear}>
                クリア
              </button>
            )}
          </form>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>メールアドレス</th>
                <th>表示名</th>
                <th>ロール</th>
                <th>登録日</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center py-4">読み込み中...</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={5} className="text-danger py-4">{error}</td>
                </tr>
              )}
              {!loading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-4" style={{ color: '#8a94ad' }}>
                    {hasActiveFilters ? '条件に一致するユーザーがいません' : 'データがありません'}
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                items.map((u) => (
                  <tr key={u.id}>
                    <td className="text-nowrap">
                      <code className="small" style={{ color: '#525b75' }}>{(u.id || '').slice(0, 8)}...</code>
                    </td>
                    <td>{u.email || '-'}</td>
                    <td>{u.displayName || '-'}</td>
                    <td>
                      <span
                        className={`badge bg-${u.role === 'admin' ? 'danger' : u.role === 'store_owner' ? 'info' : 'secondary'}`}
                      >
                        {ROLE_LABELS[u.role] || u.role || '一般'}
                      </span>
                    </td>
                    <td className="text-nowrap">{fmtDate(u.createdAt)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <Pagination
            hasMore={hasMore}
            hasPrev={cursorHistory.length > 0}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
            total={total}
            page={cursorHistory.length + 1}
            pageSize={20}
            itemCount={items.length}
          />
        </div>
      </div>
    </Layout>
  );
}
